import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import styled from 'styled-components';
import {
	Button,
	DropdownSelect,
	EmptyTableBody,
	LoadingPage,
	PaginationFooter,
	palette,
	rem,
	StylovyzeTooltip,
	Text,
} from '@innovyze/stylovyze';
import RuleIcon from '@mui/icons-material/Rule';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Grid, Stack } from '@mui/material';

import { selectFailureDefinitionsCards } from '@Selectors/failureDefinitions.selector';
import { listAllFailureDefinitions, updateFailureDefinitionPublishedStatus } from '@Actions/failureDefinition.actions';
import { useGlobalization } from '@Translations';
import { FailureDefinition } from '@Types';

import { FailureDefinitionCard } from './FailureDefinitionCard.component';
import { DefineFailureDefinitionDialog } from './DefineFailureDefinitionDialog.component';

const Bar = styled.div`
	width: 100%;
	background-color: #dcdcdc;
	padding: 10px;
`;

export const FailureDefinitionsList = () => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();

	const [sortDescending, setSortDescending] = useState<boolean>(true);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(10);
	const [openFailureDefinitionDialog, setOpenFailureDefinitionDialog] = useState<boolean>(false);

	const failureDefinitionsCards = selectFailureDefinitionsCards();

	useEffect(() => {
		dispatch(listAllFailureDefinitions());
	}, [dispatch]);

	const createFailureDefinitionButton = (
		<Button variant="contained" color="primary" disabled={false} onClick={() => setOpenFailureDefinitionDialog(true)}>
			{t('Create Failure Definition')}
		</Button>
	);

	// Sort and paginate the data locally since we get all of it
	const { paginatedData, totalPages, totalItems } = React.useMemo(() => {
		if (!failureDefinitionsCards.failureDefinitions || failureDefinitionsCards.failureDefinitions.length === 0) {
			return { paginatedData: [], totalPages: 1, totalItems: 0 };
		}
		// Sort the data locally by updatedAt date
		const sorted = [...failureDefinitionsCards.failureDefinitions].sort((a, b) => {
			const aDate = new Date(a.updatedAt || '');
			const bDate = new Date(b.updatedAt || '');

			// Handle invalid dates by putting them at the end
			if (isNaN(aDate.getTime()) && isNaN(bDate.getTime())) return 0;
			if (isNaN(aDate.getTime())) return 1;
			if (isNaN(bDate.getTime())) return -1;

			// Sort by date based on sortDescending flag
			return sortDescending ? bDate.getTime() - aDate.getTime() : aDate.getTime() - bDate.getTime();
		});

		// paginate locally
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		const paginatedData = sorted.slice(startIndex, endIndex);
		const totalPages = Math.ceil(sorted.length / pageSize);

		return { paginatedData, totalPages, totalItems: sorted.length };
	}, [failureDefinitionsCards.failureDefinitions, sortDescending, currentPage, pageSize]);

	React.useEffect(() => {
		setCurrentPage(1);
	}, [sortDescending]);

	const handlePublishToggle = (failureDefinition: FailureDefinition, published: boolean) => {
		dispatch(
			updateFailureDefinitionPublishedStatus({
				failureDefinition,
				published,
			}),
		);
	};

	const emptyView = (
		<Stack>
			<Bar>
				<Stack direction={'row'} spacing={1}>
					<Text variant="heading-medium-strong">{t('Failure Definitions')}</Text>
					<StylovyzeTooltip
						heading={t('Create failure definitions to improve predictions')}
						content={t(
							'Define what counts as failure using your inspection data, events, or tasks. ' +
								'This unlocks survival curves built from your own history. ' +
								'Optional - you can also use industry curves or define your own.',
						)}
						placement="right"
						padded
						componentsProps={{
							tooltip: {
								sx: {
									fontSize: '14px',
								},
							},
						}}>
						<InfoOutlinedIcon sx={{ fontSize: 'x-large', color: palette.secondary.main }} />
					</StylovyzeTooltip>
				</Stack>
			</Bar>
			<div
				style={{
					padding: '25px',
				}}>
				<EmptyTableBody
					icon={
						<div
							style={{
								height: '72px',
								width: '72px',
								paddingBottom: '10px',
							}}>
							<RuleIcon
								style={{
									height: '72px',
									width: '72px',
								}}
							/>
						</div>
					}
					title={t('Create Failure Definition to improve prediction')}
					subtitle={t(
						'Define what counts as failure using your inspection data, events or tasks. This unlocks survival curves built on your own history. Optional - you can also use industry curve or define your own.',
					)}
					CTA={createFailureDefinitionButton}
				/>
			</div>
		</Stack>
	);

	const viewWithCards = (
		<Stack>
			<Bar>
				<Stack direction="row" justifyContent="space-between">
					<Stack direction={'row'} spacing={1}>
						<Text variant="heading-medium-strong">{t('Failure Definitions')}</Text>
						<StylovyzeTooltip
							heading={t('Create failure definitions to improve predictions')}
							content={t(
								'Define what counts as failure using your inspection data, events, or tasks. ' +
									'This unlocks survival curves built from your own history. ' +
									'Optional - you can also use industry curves or define your own.',
							)}
							placement="right"
							padded
							componentsProps={{
								tooltip: {
									sx: {
										fontSize: '14px',
									},
								},
							}}>
							<InfoOutlinedIcon sx={{ fontSize: 'x-large', color: palette.secondary.main }} />
						</StylovyzeTooltip>
					</Stack>

					{createFailureDefinitionButton}
				</Stack>
			</Bar>
			<div
				style={{
					padding: '25px',
				}}>
				{failureDefinitionsCards.isLoading ? (
					<LoadingPage />
				) : (
					<>
						<DropdownSelect
							currentSelection={sortDescending ? 'desc' : 'asc'}
							title={t('Sort by Updated Date')}
							options={[
								{
									value: t('Modified At - Descending'),
									key: 'desc',
								},
								{
									value: t('Modified At - Ascending'),
									key: 'asc',
								},
							]}
							onChange={e => {
								setSortDescending(e.target.value === 'desc');
							}}
						/>
						<Grid container spacing={2} direction="row" style={{ padding: `${rem(16)} ${rem(0)}` }}>
							{paginatedData.map(card => (
								<Grid xs={12} sm={6} md={4} lg={3} xl={3} item key={card.id}>
									<FailureDefinitionCard failureDefinition={card} onPublishToggle={handlePublishToggle} />
								</Grid>
							))}
						</Grid>
						<Stack direction="row" justifyContent="space-between">
							<DropdownSelect
								inlineStyle={{ minWidth: '10%' }}
								currentSelection={pageSize.toString()}
								title={t('Definitions per page')}
								options={[
									{
										value: t('5'),
										key: '5',
									},
									{
										value: t('10'),
										key: '10',
									},
									{
										value: t('15'),
										key: '15',
									},
									{
										value: t('25'),
										key: '25',
									},
									{
										value: t('50'),
										key: '50',
									},
									{
										value: t('100'),
										key: '100',
									},
								]}
								onChange={e => {
									setPageSize(Number(e.target.value));
									setCurrentPage(1); // Reset to first page when changing page size
								}}
							/>
							<PaginationFooter
								paginationInfo={{
									total: totalItems,
									previous: currentPage > 1 ? currentPage - 1 : 1,
									next: currentPage < totalPages ? currentPage + 1 : totalPages,
									current: currentPage,
									pages: totalPages,
								}}
								onChange={(_e, page: number) => setCurrentPage(page)}
							/>
						</Stack>
					</>
				)}
			</div>
		</Stack>
	);

	return (
		<>
			{failureDefinitionsCards.isLoading || failureDefinitionsCards.failureDefinitions.length > 0
				? viewWithCards
				: emptyView}
			<DefineFailureDefinitionDialog isOpen={openFailureDefinitionDialog} setOpen={setOpenFailureDefinitionDialog} />
		</>
	);
};
